import React from 'react'
import { useQuery } from 'react-query'
import { Text, Button, Loader, HTMLText } from '@crossfit/ui'
import type { Image } from 'types'
import * as contentService from 'services/content'
import InnerContent from 'components/InnerContent'
import PageHeader from 'components/PageHeader'
import ImageGallery, { ImageGalleryImage } from 'components/ImageGallery'
import usePageTitle from 'hooks/usePageTitle'
import NotAuthorized from 'components/NotAuthorized'
import style from './style.module.css'
import {
  ApplicationItemStatus,
  useAffiliateApplication,
} from 'hooks/useAffiliateAplication'
import FullWidthPromo from 'components/FullWidthPromo'
import trainerLarge from './trainerLarge.png'
import trainerSmall from './trainerSmall.png'
import AffiliateApplication from 'components/ApplicationStatus'
import { trackEvent } from 'services/segment'
import { useSelector } from 'react-redux'
import { getMyAffiliates } from 'store/selectors'
import { useMyActiveSubscriptions } from 'hooks/subscriptions'
import { canAccessAffiliateStarterKit } from 'components/Auth/abilities'

type Content = {
  headlineText: string
  subheadlineText: string
  bodyText: string
  image: Image
  accessCourseLinkUrl: string
  accessCourseLinkText: string
}

function useStarterKit() {
  const { selectedAffiliate } = useSelector(getMyAffiliates)
  const subscriptions = useMyActiveSubscriptions()
  const starterKitAccess = canAccessAffiliateStarterKit(
    selectedAffiliate,
    subscriptions,
  )
  const contentQuery = useQuery(
    'starter_kit',
    () => contentService.fetchPage<Content>('/starter-kit'),
    { enabled: starterKitAccess },
  )

  return {
    hasAccess: starterKitAccess,
    content: contentQuery.data,
    isLoading: contentQuery.isLoading,
  }
}

function StarterKitPage() {
  const { content, isLoading, hasAccess } = useStarterKit()
  const applicationStatus = useAffiliateApplication()
  const { selectedAffiliate: affiliate } = useSelector(getMyAffiliates)

  usePageTitle(
    `CrossFit Affiliate Toolkit${content ? ` | ${content.headlineText}` : ''}`,
  )

  const fullWidthPromoProps = {
    buttonColor: 'red' as const,
    cta: {
      bodyText: `There are several enemies of wellness and the soda industry is their ringleader. They've poured billions into obscuring the danger behind their products. Here's how we fight back`,
      headlineText: 'Sodas are Killing Us.',
      link: {
        text: 'Learn More',
        url: '/',
      },
    },
    image: {
      src: {
        large: trainerLarge,
        largeWidth: 1678,
        medium: trainerSmall,
        mediumWidth: 1000,
        small: trainerSmall,
        smallWidth: 454,
      },
    },
    kickerHeadlineText: 'What we Believe',
  }

  if (isLoading || applicationStatus == null) {
    return <Loader />
  }

  if (!hasAccess) {
    return <NotAuthorized />
  }

  return (
    <>
      <PageHeader subheadlineText={content.subheadlineText}>
        {content.headlineText}
      </PageHeader>
      <InnerContent className={style.innerContent} as='main' variant='xlarge'>
        <ImageGallery className={style.imageGallery}>
          <ImageGalleryImage {...content.image} />
        </ImageGallery>
        <div className={style.content}>
          <Text className={style.subHeading}></Text>
          <HTMLText className={style.htmlText}>{content.bodyText}</HTMLText>
          <Button
            className={style.primaryButton}
            as='button'
            onClick={() => {
              trackEvent({
                event: 'Affiliate Starter Kit Access Course Clicked',
                component: 'Starter Kit Page',
                text: content.accessCourseLinkText,
                linkUrl: content.accessCourseLinkUrl,
                location: 'Affiliate Toolkit',
                affiliateId: affiliate?.affiliateId,
                userRole:
                  affiliate?.roles?.length > 0 ? affiliate?.roles[0] : null,
                groupId: affiliate?.groupId,
              })
              location.href = content.accessCourseLinkUrl
            }}
          >
            {content.accessCourseLinkText}
          </Button>
        </div>
      </InnerContent>

      {applicationStatus?.data?.application.status ===
        ApplicationItemStatus.DEFAULT &&
        !affiliate && <FullWidthPromo {...fullWidthPromoProps} />}

      {applicationStatus?.data?.application.status !==
        ApplicationItemStatus.DEFAULT &&
        !affiliate && <AffiliateApplication />}
    </>
  )
}

export default StarterKitPage
